import type { ContentPiece } from "../../../../shared/types";
import { cn } from "../../lib/utils";

interface PublishStatusProps {
  pieces: ContentPiece[];
  onRetry?: (pieceId: string) => void;
}

const PLATFORM_LABELS: Record<string, string> = {
  linkedin: "LinkedIn",
  twitter: "Twitter / X",
  instagram: "Instagram",
  threads: "Threads",
  tiktok: "TikTok",
  youtube: "YouTube",
};

function StatusDot({ status }: { status: ContentPiece["publish_status"] }) {
  if (status === "publishing") {
    return (
      <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
    );
  }
  if (status === "published") {
    return (
      <svg
        className="h-4 w-4 text-green-500"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    );
  }
  if (status === "failed") {
    return (
      <svg
        className="h-4 w-4 text-red-500"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  }
  // queued or null
  return (
    <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
  );
}

function StatusLabel({ piece, onRetry }: { piece: ContentPiece; onRetry?: (id: string) => void }) {
  const status = piece.publish_status;

  if (status === "queued") {
    return <span className="text-sm text-slate-500">Queued</span>;
  }

  if (status === "publishing") {
    return <span className="text-sm text-blue-600">Publishing...</span>;
  }

  if (status === "published") {
    return (
      <span className="flex items-center gap-2">
        <span className="text-sm text-green-600">Published</span>
        {piece.platform_post_url && (
          <a
            href={piece.platform_post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-brand-600 underline hover:text-brand-700"
          >
            View post
          </a>
        )}
      </span>
    );
  }

  if (status === "failed") {
    return (
      <span className="flex items-center gap-2">
        <span className="text-sm text-red-600">
          {piece.publish_error || "Failed"}
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={() => onRetry(piece.id)}
            className="text-xs font-medium text-indigo-600 underline hover:text-indigo-700"
          >
            Retry
          </button>
        )}
      </span>
    );
  }

  return null;
}

export function PublishStatus({ pieces, onRetry }: PublishStatusProps) {
  // Only show pieces that have a publish_status set
  const publishingPieces = pieces.filter((p) => p.publish_status !== null);

  if (publishingPieces.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">
        Publishing Status
      </h3>
      <ul className="space-y-2">
        {publishingPieces.map((piece) => (
          <li
            key={piece.id}
            className="flex items-center gap-3"
          >
            <StatusDot status={piece.publish_status} />
            <span className="text-sm font-medium text-slate-700 min-w-[100px]">
              {PLATFORM_LABELS[piece.platform] || piece.platform}
            </span>
            <StatusLabel piece={piece} onRetry={onRetry} />
          </li>
        ))}
      </ul>
    </div>
  );
}
